import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Typography from "@mui/material/Typography";
import { CheckboxIcon, CheckboxOutlineIcon } from "app/icons";
import React from "react";
import { useTranslation } from "react-i18next";

import { WALL_TYPE_OPTIONS } from "../../../data";
import { HEIGHT_KEY, NOTE, TYPE_KEY } from "../../../hooks/useQuoteBoxStructure";
import {
  CHECKBOX_SIZE,
  NoteField,
  NumberField,
  SelectField,
} from "./FormFields";
import { useStyles } from "../styles";

type BoxChildren = { children: React.ReactNode };
type ToggleProps = {
  labelKey: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
};

const checkboxControl = {
  disableRipple: true,
  icon: <CheckboxOutlineIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />,
  checkedIcon: <CheckboxIcon width={CHECKBOX_SIZE} height={CHECKBOX_SIZE} />,
  sx: { p: 0, width: CHECKBOX_SIZE, height: CHECKBOX_SIZE },
};

export const OptionCheckbox = ({
  labelKey,
  checked,
  onChange,
  title,
}: ToggleProps & { title?: boolean }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.optionCheckboxRow}>
      <Checkbox
        {...checkboxControl}
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      <Typography
        className={
          title ? styles.optionCheckboxTitle : styles.optionCheckboxLabel
        }
      >
        {t(labelKey)}
      </Typography>
    </Box>
  );
};

export const OptionCard = ({
  enabled,
  children,
}: BoxChildren & { enabled: boolean }) => {
  const styles = useStyles();
  const active = enabled ? ` ${styles.optionCardActive}` : "";

  return <Box className={`${styles.optionCard}${active}`}>{children}</Box>;
};

export const OptionHeader = (props: ToggleProps) => {
  const styles = useStyles();

  return (
    <>
      <OptionCheckbox {...props} title />
      <Box className={styles.optionCardDivider} />
    </>
  );
};

export const FieldsRow = ({ children }: BoxChildren) => {
  const styles = useStyles();
  return <Box className={styles.optionFieldsRow}>{children}</Box>;
};

export const CardRow = ({
  columns = 2,
  children,
}: BoxChildren & { columns?: number }) => {
  const styles = useStyles();

  return (
    <Box
      className={styles.optionCardRow}
      sx={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
    >
      {children}
    </Box>
  );
};

export const StructureCard = ({
  enabled,
  titleKey,
  onToggle,
  alignFields,
  children,
}: BoxChildren & {
  enabled: boolean;
  titleKey: string;
  onToggle: (enabled: boolean) => void;
  alignFields?: boolean;
}) => {
  const styles = useStyles();

  return (
    <OptionCard enabled={enabled}>
      <OptionHeader labelKey={titleKey} checked={enabled} onChange={onToggle} />
      <Box
        className={
          alignFields ? styles.optionCardBodyAligned : styles.optionCardBody
        }
      >
        {children}
      </Box>
    </OptionCard>
  );
};

export const StructureSection = ({
  titleKey,
  plain,
  children,
}: BoxChildren & { titleKey: string; plain?: boolean }) => {
  const { t } = useTranslation();
  const styles = useStyles();

  return (
    <Box className={styles.formSection}>
      <Box className={plain ? styles.sectionHeaderPlain : styles.sectionHeader}>
        <Typography className={styles.sectionTitle}>{t(titleKey)}</Typography>
      </Box>
      <Box className={styles.optionSectionContent}>{children}</Box>
    </Box>
  );
};

export const TypeHeightRow = ({
  typeValue,
  heightValue,
  onTypeChange,
  onHeightChange,
}: {
  typeValue: string;
  heightValue: string;
  onTypeChange: (value: string) => void;
  onHeightChange: (value: string) => void;
}) => (
  <FieldsRow>
    <SelectField
      labelKey={TYPE_KEY}
      value={typeValue}
      options={WALL_TYPE_OPTIONS}
      onChange={onTypeChange}
      grow
    />
    <NumberField
      labelKey={HEIGHT_KEY}
      value={heightValue}
      onChange={onHeightChange}
      heightStepper
    />
  </FieldsRow>
);

export const CheckboxNote = ({
  checkboxLabelKey,
  checked,
  note,
  onCheck,
  onNote,
  title,
}: {
  checkboxLabelKey: string;
  checked: boolean;
  note: string;
  onCheck: (checked: boolean) => void;
  onNote: (note: string) => void;
  title?: boolean;
}) => {
  const styles = useStyles();

  return (
    <Box className={styles.optionNoteColumn}>
      <OptionCheckbox
        labelKey={checkboxLabelKey}
        checked={checked}
        onChange={onCheck}
        title={title}
      />
      <NoteField
        labelKey={NOTE}
        placeholderKey={NOTE}
        value={note}
        onChange={onNote}
      />
    </Box>
  );
};
